import React from "react";
import Button from "../../../../Components/Common/Button/Button";
import Table from "../../../../Components/Table/Table";
import { AssignRole, DrawerMode, ProjectInspector } from "./AssignmentDrawer";
import EmptyState from "Components/Common/EmptyState/EmptyState";
import EmptyProjectsIcon from "Assets/Img/EmptyProjectsIcon";

interface ApiProject extends Record<string, unknown> {
  id: string;
  name: string;
  type?: string;
  status?: string;
  inspectors: ProjectInspector[];
  overdue?: boolean;
  nextInspection?: string;
  address?: string;
  lender?: { companyName: string };
  gc?: { companyName: string };
  drawNumber?: number;
  inspectionNumber?: number;
}

interface Column {
  header: string;
  render: (project: ApiProject) => React.ReactNode;
}

interface ProjectTableProps {
  projects: ApiProject[];
  isFiltered?: boolean;
  onOpenDrawer: (project: ApiProject, mode: DrawerMode) => void;
  onRemoveInspector: (
    projectId: string,
    assignmentId: string,
    role: AssignRole,
  ) => void;
}

const ProjectTable = ({
  projects,
  isFiltered = false,
  onOpenDrawer,
  onRemoveInspector,
}: ProjectTableProps) => {
  const getRowClassName = (project: ApiProject) =>
    project.inspectors.some((inspector) => inspector.role === "primary")
      ? ""
      : "unassignedRow";
  const columns: Column[] = [
    {
      header: "PROJECT",
      render: (project) => (
        <div className="projectCell">
          <p className="projName">{project.name}</p>
          <p className="projMeta">
            {project?.address} · {project?.lender?.companyName} ·{" "}
            {project?.gc?.companyName}
          </p>
        </div>
      ),
    },
    {
      header: "TYPE / STATUS",
      render: (project) => (
        <div className="typeSec">
          <span
            className={`projTypeBadge ${
              project.type === "commercial" ? "ptCom" : "ptRes"
            }`}
          >
            {project.type === "commercial" ? "Commercial" : "Residential"}
          </span>
          <span
            className={`badge ${
              project.status === "active" ? "bActive" : "bPrecon"
            }`}
          >
            <span className="dot" />
            {project.status === "active" ? "In Progress" : "Pre-Con"}
          </span>
        </div>
      ),
    },
    {
      header: "ASSIGNED INSPECTOR(S)",
      render: (project) => (
        <div className="inspectorPills">
          {project.inspectors?.length ? (
            project.inspectors.map((entry) => {
              const { assignmentId, role, inspector } = entry;
              const firstName = inspector?.firstName ?? "";
              const lastName = inspector?.lastName ?? "";
              const initials =
                `${firstName[0] ?? ""}${lastName[0] ?? ""}`.toUpperCase();
              const fullName = `${firstName} ${lastName}`.trim();

              return (
                <span
                  className={`inspPill ${role === "primary" ? "primary" : "cover"}`}
                  key={assignmentId}
                >
                  <span
                    className={`pillAvatar ${role === "primary" ? "jm" : "ts"}`}
                  >
                    {initials}
                  </span>
                  {fullName}
                  {role === "coverage" && <em>(cover)</em>}
                  <Button
                    className="removeBtn"
                    onClick={() =>
                      onRemoveInspector(project.id, assignmentId, role)
                    }
                  >
                    <span>×</span>
                  </Button>
                </span>
              );
            })
          ) : (
            <span className="inspPill unassigned">⚠ No inspector assigned</span>
          )}
        </div>
      ),
    },
    {
      header: "DRAW / INSPECTION",
      render: (project) => {
        const { status } = project;
        let label = "—";
        if (status === "active" || status === "draw_request") label = "Draw";
        else if (status === "pre_construction") label = "Pre-Con";
        else if (status === "completed") label = "Completed";
        return (
          <div className="drawBadge">
            {label}{" "}
            <strong>
              {project.drawNumber} · {project.inspectionNumber} inspections
            </strong>
          </div>
        );
      },
    },
    {
      header: "NEXT INSPECTION",
      render: (project) => (
        <div className={`nextInspection ${project.overdue ? "overdue" : ""}`}>
          {project.nextInspection} Not Available
        </div>
      ),
    },
    {
      header: "ACTIONS",
      render: (project) => {
        const hasPrimary = project.inspectors.length;

        return (
          <div className="actionsCell">
            {!hasPrimary ? (
              <Button
                className="btn btnDanger btnSm"
                onClick={() => onOpenDrawer(project, "assign")}
              >
                Assign Now
              </Button>
            ) : (
              <>
                <Button
                  className="btn btnPrimary btnSm"
                  onClick={() => onOpenDrawer(project, "coverage")}
                >
                  + Coverage
                </Button>
                <Button
                  className="btn btnSecondary btnSm"
                  onClick={() => onOpenDrawer(project, "reassign")}
                >
                  Reassign
                </Button>
              </>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <Table
      data={projects}
      columns={columns}
      rowClassName={getRowClassName}
      fallbackUI={
        <EmptyState
          noBorder={true}
          icon={<EmptyProjectsIcon />}
          title={isFiltered ? "No Results Found" : "No Projects Yet"}
          subtitle={
            isFiltered
              ? "No projects match your current search or filter criteria. Try adjusting your filters."
              : "Projects added to the platform will appear here once they are created."
          }
        />
      }
    />
  );
};

export default ProjectTable;
